import {
    ButtonPrimary,
    ButtonSecondary,
    Label,
    ModalDialog,
    MultilineInput,
    TextInput,
} from '@amelio/ui-library';
import { useEffect, useId, useState } from 'react';

import type { FormEvent } from 'react';

import { useAmelioTranslations } from '@/hooks';

interface MeetingTopicModalProps {
    isOpen: boolean;
    initialSubject?: string;
    initialNotes?: string;
    isSaving?: boolean;
    onClose: () => void;
    onSave: (values: { subject: string; notes: string }) => void;
    dataTestId?: string;
}

export const MeetingTopicModal = ({
    isOpen,
    initialSubject = '',
    initialNotes = '',
    isSaving = false,
    onClose,
    onSave,
    dataTestId = 'meeting-topic-modal',
}: MeetingTopicModalProps) => {
    const { t } = useAmelioTranslations();
    const subjectId = useId();
    const notesId = useId();

    const [subject, setSubject] = useState(initialSubject);
    const [notes, setNotes] = useState(initialNotes);

    // Reset the fields to the topic being edited each time the modal opens.
    useEffect(() => {
        if (isOpen) {
            setSubject(initialSubject);
            setNotes(initialNotes);
        }
    }, [isOpen, initialSubject, initialNotes]);

    const trimmedSubject = subject.trim();
    const canSave = trimmedSubject.length > 0 && !isSaving;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (canSave) {
            onSave({ subject: trimmedSubject, notes });
        }
    };

    // A saved topic always has a subject, so an empty initial subject means a new one.
    const title = initialSubject
        ? t('managerAssessmentEvaluation.meetingTopics.modalEditTitle')
        : t('managerAssessmentEvaluation.meetingTopics.addPoint');

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size={'md'}
            closeAriaLabel={t('common.close')}
            onSubmit={handleSubmit}
            backdropBlur
            dataTestId={dataTestId}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'sm'}
                        onClick={onClose}
                        dataTestId={'meeting-topic-cancel'}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonPrimary
                        type={'submit'}
                        size={'sm'}
                        disabled={!canSave}
                        loading={isSaving}
                        // The library swaps the label for a spinner while saving; the name holds.
                        ariaLabel={t('managerAssessmentEvaluation.meetingTopics.saveChanges')}
                        dataTestId={'meeting-topic-save'}
                    >
                        {t('managerAssessmentEvaluation.meetingTopics.saveChanges')}
                    </ButtonPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-2'}>
                    <Label htmlFor={subjectId}>
                        {t('managerAssessmentEvaluation.meetingTopics.modalSubjectLabel')}
                    </Label>

                    <TextInput
                        id={subjectId}
                        value={subject}
                        onChange={setSubject}
                        dataTestId={'meeting-topic-subject'}
                    />
                </div>

                <div className={'flex flex-col gap-2'}>
                    <Label htmlFor={notesId}>
                        {t('managerAssessmentEvaluation.meetingTopics.modalNotesLabel')}
                    </Label>

                    <MultilineInput
                        id={notesId}
                        value={notes}
                        onChange={setNotes}
                        placeholder={t(
                            'managerAssessmentEvaluation.meetingTopics.modalNotesPlaceholder',
                        )}
                        rows={6}
                        dataTestId={'meeting-topic-notes'}
                    />
                </div>
            </div>
        </ModalDialog>
    );
};
